import { createContext, useContext, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Box, Accordion, AccordionSummary, AccordionDetails, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ReactMarkdown from 'react-markdown';
import ThreeTablesModal from './SearchResultModal';
import getUserId from '../functions/getUserId';
import { buildTickerIndex, splitTickers, remarkTickerLinks, tickerFromHref } from '../functions/tickerLinks';

// DAS-2506 format: a "🕒 timestamp" line followed by "🔹 <Step>:" blocks,
// stored newest-first. Shared by the analysis dialog and the LLM Advice tab.
export function parseAnalysis(raw) {
  if (!raw || typeof raw !== 'string') return { timestamp: null, sections: [] };
  const tsMatch = raw.match(/🕒\s*([^\n]+)/);
  const timestamp = tsMatch ? tsMatch[1].trim() : null;
  const sections = raw
    .split('🔹')
    .slice(1)
    .map((part) => {
      const nl = part.indexOf('\n');
      const title = (nl === -1 ? part : part.slice(0, nl)).trim().replace(/:\s*$/, '');
      const content = (nl === -1 ? '' : part.slice(nl + 1)).trim();
      return { title, content };
    })
    .filter((s) => s.title);
  return { timestamp, sections };
}

// DAS-2904: the stored step titles predate the DAS-2522 role re-routing, so
// they name the wrong models (Claude authors the advice, DeepSeek is the
// skeptic). Display neutral role labels; the stored blob stays untouched so
// every historical analysis keeps parsing.
const STEP_LABELS = [
  { match: 'risk profile', label: 'Risk profile' },
  { match: 'initial advice', label: 'First draft advice' },
  { match: 'challenge', label: 'Fact check and challenge' },
  { match: 'reconsidered', label: 'Revised advice' },
  { match: 'review', label: 'Pre-mortem: how this advice could fail' },
  { match: 'final validation', label: 'Final advice' },
  { match: 'transaction', label: 'Recommended action' },
  { match: 'lane verdicts', label: 'The four questions' },      // DAS-3005
  { match: 'plan check', label: 'Where you stand against your plan' },   // DAS-3020
  { match: 'debate skipped', label: 'Debate skipped' },
];

export function stepLabel(title) {
  const t = (title || '').toLowerCase();
  const hit = STEP_LABELS.find((s) => t.includes(s.match));
  return hit ? hit.label : title;
}

// DAS-3249: every ticker in the analysis text opens the standard search result.
// The tickers recognised are the ones the user knows — holdings, open alerts,
// favorites, plus whatever the caller adds (the advice records) — see tickerLinks.js.
const TickerLinkContext = createContext(null);

const tickersOf = (list, field) => (Array.isArray(list) ? list.map((r) => r?.[field]) : []);

export function TickerLinkProvider({ extraTickers, children }) {
  const portfolio = useSelector((state) => state?.portfolio?.data?.portfolio);
  const pfAlerts = useSelector((state) => state?.portfolio?.data?.pf_alert);
  const oppAlerts = useSelector((state) => state?.portfolio?.data?.opportunities_alerts);
  const favAlerts = useSelector((state) => state?.favorites?.data?.favorites_alert_data);
  const favorites = useSelector((state) => state?.favorites?.data?.data);
  const [openTicker, setOpenTicker] = useState(null);
  const extraKey = (extraTickers || []).join('|');

  const value = useMemo(() => {
    const index = buildTickerIndex(
      tickersOf(portfolio, 'Ticker'), tickersOf(pfAlerts, 'ticker'), tickersOf(oppAlerts, 'ticker'),
      tickersOf(favAlerts, 'ticker'), tickersOf(favorites, 'Ticker'), extraTickers || [],
    );
    return { index, plugins: [remarkTickerLinks(index)], open: setOpenTicker };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portfolio, pfAlerts, oppAlerts, favAlerts, favorites, extraKey]);

  return (
    <TickerLinkContext.Provider value={value}>
      {children}
      {openTicker && (
        <ThreeTablesModal
          tickerValue={openTicker}
          open={!!openTicker}
          onClose={() => setOpenTicker(null)}
          userId={getUserId()}
        />
      )}
    </TickerLinkContext.Provider>
  );
}

function TickerLink({ ticker, children }) {
  const ctx = useContext(TickerLinkContext);
  return (
    <span
      data-testid="das3249-ticker"
      role="button"
      style={{ color: '#1677ff', cursor: 'pointer' }}
      onClick={(e) => { e.stopPropagation(); ctx.open(ticker); }}
    >
      {children}
    </span>
  );
}

const markdownLink = ({ href, children, node, ...rest }) => {
  const ticker = tickerFromHref(href);
  return ticker ? <TickerLink ticker={ticker}>{children}</TickerLink> : <a href={href} {...rest}>{children}</a>;
};

// Plain text with the tickers clickable (the legacy unstructured analysis).
function TickerText({ text }) {
  const ctx = useContext(TickerLinkContext);
  if (!ctx) return text;
  return splitTickers(text, ctx.index).map((p, i) =>
    p.ticker ? <TickerLink key={i} ticker={p.ticker}>{p.text}</TickerLink> : <span key={i}>{p.text}</span>);
}

export function TickerMarkdown({ content }) {
  const ctx = useContext(TickerLinkContext);
  return ctx
    ? <ReactMarkdown remarkPlugins={ctx.plugins} components={{ a: markdownLink }}>{content}</ReactMarkdown>
    : <ReactMarkdown>{content}</ReactMarkdown>;
}

function Markdown({ content }) {
  return (
    <Box sx={{ fontSize: '13px', '& p': { mt: 0.5, mb: 0.5 } }}>
      <TickerMarkdown content={content} />
    </Box>
  );
}

// The reading order the owner asked for: the corrected final advice first,
// the drafts it retracted foldable behind one toggle. Callers that render
// their own recommended-action box (the analysis dialog) leave
// showTransaction off; the LLM Advice tab's full-reasoning modal turns it on.
// DAS-3249: a caller that already wraps the page in TickerLinkProvider shares its
// links; otherwise the body brings its own.
export default function LLMAnalysisBody({ extraTickers, ...props }) {
  const ctx = useContext(TickerLinkContext);
  return ctx
    ? <AnalysisContent {...props} />
    : <TickerLinkProvider extraTickers={extraTickers}><AnalysisContent {...props} /></TickerLinkProvider>;
}

function AnalysisContent({ analysis, showTransaction = false, showTimestamp = false }) {
  const { timestamp, sections } = parseAnalysis(analysis);

  if (!sections.length) {
    // Legacy/unstructured text (or an error message): show as-is.
    return <Box sx={{ whiteSpace: 'pre-wrap' }}><TickerText text={analysis || ''} /></Box>;
  }

  const transaction = sections.find((s) => s.title.toLowerCase().includes('transaction'));
  const lanes = sections.find((s) => s.title.toLowerCase().includes('lane verdicts'));   // DAS-3005
  const plan = sections.find((s) => s.title.toLowerCase().includes('plan check'));         // DAS-3020
  const finalAdvice = sections.find((s) => s.title.toLowerCase().includes('final validation'));
  const debate = sections
    .filter((s) => s !== transaction && s !== finalAdvice && s !== lanes && s !== plan)
    .reverse(); // stored newest-first -> chronological reading order

  return (
    <>
      {showTimestamp && timestamp && (
        <Typography sx={{ fontSize: '12.5px', color: 'text.secondary', mb: 1 }}>🕒 {timestamp}</Typography>
      )}

      {showTransaction && transaction && (
        <Box
          data-testid="das2904-recommended-action"
          sx={{ p: 1.5, mb: 2, border: '1px solid', borderColor: 'primary.main', borderRadius: 1, bgcolor: 'action.hover' }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>📌 Recommended action</Typography>
          <Markdown content={transaction.content} />
        </Box>
      )}

      {plan && (
        <Box data-testid="das-plan-check" sx={{ p: 1.5, mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>📐 Where you stand against your plan — computed from live numbers, not from the document</Typography>
          <Markdown content={plan.content} />
        </Box>
      )}

      {lanes && (
        <Box data-testid="das-lane-verdicts" sx={{ p: 1.5, mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>🧭 The four questions behind it — risk, strategy, cash surplus, swap</Typography>
          <Markdown content={lanes.content} />
        </Box>
      )}

      {finalAdvice && (
        <Box data-testid="das2904-final-advice" sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>✅ Strategy after the debate — the Recommended action is what to act on; it says so when it differs</Typography>
          <Markdown content={finalAdvice.content} />
        </Box>
      )}

      {debate.length > 0 && (
        <Accordion disableGutters data-testid="das2904-debate-toggle" TransitionProps={{ unmountOnExit: true }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ fontWeight: 600, fontSize: '14px' }}>
              Show the debate — how this advice was reached ({debate.length} steps)
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography sx={{ fontSize: '12.5px', color: 'text.secondary', mb: 1.5 }}>
              Early steps can contain advice that later steps retract — the Final advice above is the version that stands.
            </Typography>
            {debate.map((s, i) => (
              <Box key={i} sx={{ mb: 2 }}>
                <Typography sx={{ fontWeight: 600, fontSize: '13.5px', mb: 0.5 }}>{stepLabel(s.title)}</Typography>
                <Markdown content={s.content} />
              </Box>
            ))}
          </AccordionDetails>
        </Accordion>
      )}
    </>
  );
}
